import { http } from '@/app/lib/http/client'

// ---------------------------------------------------------
// 1. Interfaces (Como vienen de la base de datos/API o mocks)
// ---------------------------------------------------------

export interface Supplier {
  id: number
  name: string
  contact: string
  phone: string
  email: string
  products: number
}

export interface PurchaseOrder {
  id: string
  supplier: string
  date: string
  status: 'pending' | 'received' | 'partial'
  items: number
  total: number
}

export interface ProductEntry {
  id: string
  xmlFileName: string
  supplier: string
  date: string
  entryDate: string
  paymentForm: string
  paymentMethod: string
  total: number
  items: number
  status: 'processed' | 'pending'
}

export interface PurchaseTaxRecord {
  id: number
  tax_id: number | null
  tax_kind: string
  tax_code: string
  tax_name: string
  factor_type: string
  tax_rate: string
  taxable_amount: string | null
  tax_amount: string | null
}

export interface PurchaseItemRecord {
  id: number
  product_id: number | null
  original_name: string
  original_description: string
  original_barcode: string
  sat_product_key_code: string | null
  sat_product_key_name: string | null
  quantity: string | null
  unit_cost_amount: string | null
  subtotal_amount: string | null
  tax_amount: string | null
  total_amount: string | null
  lot_number: string | null
  expiration_date: string | null
  match_status: string
  previous_cost_amount: string | null
  previous_sale_price_amount: string | null
  previous_profit_percentage: string | null
  proposed_cost_amount: string | null
  proposed_sale_price_amount: string | null
  proposed_profit_percentage: string | null
  price_decision: 'applied' | 'pending'
  product?: {
    id: number
    name: string
    barcode: string
    is_active: boolean
  } | null
  taxes?: PurchaseTaxRecord[]
}

export interface PurchaseRecord {
  id: number
  source: 'manual' | 'excel' | 'cfdi_xml'
  reference_number: string
  purchase_date: string
  entry_date: string
  payment_condition: 'cash' | 'credit'
  currency: string
  subtotal_amount: string | null
  tax_total_amount: string | null
  total_amount: string | null
  status: string
  price_review_status: string
  document_name: string | null
  items_count: number
  supplier: { id: number; name: string }
  payment_method: { id: number; name: string; code: string }
  created_by?: { id: number; name: string; last_name?: string | null }
  items?: PurchaseItemRecord[]
}

export interface PurchasePaginator {
  current_page: number
  data: PurchaseRecord[]
  from: number | null
  to: number | null
  per_page: number
  total: number
}

export interface PurchaseApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export interface ManualPurchaseItemPayload {
  name: string
  description: string
  barcode: string
  sat_product_key_code?: string
  sat_product_key_name?: string
  category_id: number
  department_id: number
  product_type_id: number
  unit_id: number
  quantity?: number
  cost_without_taxes?: number
  tax_id?: number
  profit_percentage?: number
  lot_number?: string
  expiration_date?: string
}

export interface ManualPurchasePayload {
  reference_number: string
  supplier_id: number
  purchase_date: string
  entry_date: string
  payment_condition: 'cash' | 'credit'
  payment_method_id: number
  items: ManualPurchaseItemPayload[]
}

export interface PurchaseExcelResolvedCatalog {
  id: number
  name: string
  requires_lot?: boolean
}

export interface PurchaseExcelPreviewRow {
  row_key: string
  row_number: number
  status: 'new_product' | 'existing_product' | 'blocked'
  imported: {
    sku: string | null
    barcode: string | null
    name: string | null
    description: string | null
    department: string | null
    category: string | null
    unit: string | null
    product_type: string | null
    sat_product_key_code: string | null
    sat_product_key_name: string | null
    quantity: number | null
    cost_without_taxes: string | null
    profit_percentage: string | null
    taxes: Array<{ name: string; type: string; percentage: number }>
  }
  resolved_catalogs: {
    category: PurchaseExcelResolvedCatalog | null
    department: PurchaseExcelResolvedCatalog | null
    unit: PurchaseExcelResolvedCatalog | null
    product_type: PurchaseExcelResolvedCatalog | null
    sat_key: ({ id: number; code: string; name: string }) | null
  }
  suggested_tax: ({ id: number; name: string; type: string; percentage: string | number }) | null
  resolved_product: {
    id: number
    name: string
    description: string | null
    barcode: string
    is_active: boolean
    is_deleted: boolean
    current_stock: number
    category: string | null
    department: string | null
    unit: string | null
    product_type: string | null
    requires_lot: boolean
    sat_product_key_code: string | null
    sat_product_key_name: string | null
    price: {
      cost_without_taxes: string
      final_price: string
      profit_percentage: string
    } | null
  } | null
  errors: string[]
  warnings: string[]
}

export interface PurchaseExcelPreview {
  file: {
    name: string
    size: number
    hash: string
    sheet_name: string | null
    stock_header: string
  }
  summary: {
    total: number
    new_products: number
    existing_products: number
    incomplete: number
    blocked: number
  }
  rows: PurchaseExcelPreviewRow[]
}

export interface ExcelPurchaseItemPayload extends Partial<ManualPurchaseItemPayload> {
  origin: 'manual' | 'excel'
  row_key: string
  row_number?: number
  barcode: string
}

export interface ExcelPurchasePayload extends Omit<ManualPurchasePayload, 'items'> {
  items: ExcelPurchaseItemPayload[]
}

export interface ProductFromXML {
  id: number
  name: string
  quantity: number
  newPrice: number
  previousPrice: number
  priceChange: number
  satKey: string
  lotNumber: string
  salePrice: number
  margin: number
  supplier: string
}

export interface SuggestedProduct {
  id: number
  name: string
  currentStock: number
  avgSales: number
  daysRemaining: number
  suggested: number
  supplier: string
}

export interface NewProductData {
  id: number
  name: string
  satKey: string
  categoryId: string
  departmentId: string
  productTypeId: string
  requiresLot?: boolean
  expiryDate: string
  lotNumber?: string
  unitId: string
  description: string
  isRegistered: boolean
  barcode?: string
  quantity?: number
  cost?: number
  margin?: number
  salePrice?: number
  taxId?: string
}

export interface PriceAdjustment {
  id: string
  supplier: string
  date: string
  purchaseId: string
  status: 'pending' | 'updated'
  items: number
  products: PriceAdjustmentProduct[]
  isEdited?: boolean
  editDate?: string
}

export interface PriceAdjustmentProduct {
  id: number
  name: string
  quantity: number
  previousCost: number
  newCost: number
  costChange: number
  previousSalePrice: number
  previousMargin: number
  newSalePrice: number
  newMargin: number
  lotNumber: string
}

// ---------------------------------------------------------
// 2. Interfaces para Crear/Editar (Lo que mandas en POST/PUT)
// ---------------------------------------------------------

export interface CreateSupplierPayload {
  name: string
  contact: string
  phone: string
  email: string
  website?: string
  catalogType?: 'url' | 'file'
}

export interface CreateProductEntryPayload {
  paymentForm: string
  paymentMethod: string
  entryDate: string
  products: ProductFromXML[]
  newProducts?: NewProductData[]
}

// ---------------------------------------------------------
// 3. Mock Data
// ---------------------------------------------------------

export const mockSuppliers: Supplier[] = [
  {
    id: 1,
    name: 'Laboratorios Pisa',
    contact: 'Juan Pérez',
    phone: '555-1234',
    email: 'ventas@pisa.com.mx',
    products: 45,
  },
  {
    id: 2,
    name: 'Farmacéutica GNP',
    contact: 'María González',
    phone: '555-5678',
    email: 'pedidos@gnp.com.mx',
    products: 38,
  },
  {
    id: 3,
    name: 'Distribuidora Farmacéutica',
    contact: 'Carlos Méndez',
    phone: '555-9012',
    email: 'contacto@distfarma.com',
    products: 52,
  },
]

export const mockProductEntries: ProductEntry[] = [
  /*
  {
    id: 'ING-001',
    xmlFileName: 'factura_pisa_202604_001.xml',
    supplier: 'Laboratorios Pisa',
    date: '2026-04-25',
    entryDate: '2026-04-26',
    paymentForm: 'Crédito',
    paymentMethod: 'Transferencia',
    total: 12500,
    items: 15,
    status: 'processed',
  },
  {
    id: 'ING-002',
    xmlFileName: 'factura_gnp_202604_045.xml',
    supplier: 'Farmacéutica GNP',
    date: '2026-04-24',
    entryDate: '2026-04-24',
    paymentForm: 'Contado',
    paymentMethod: 'Efectivo',
    total: 18900,
    items: 22,
    status: 'processed',
  },
  */
]

export const mockSuggestedProducts: SuggestedProduct[] = [
  {
    id: 1,
    name: 'Ibuprofeno 400mg',
    currentStock: 15,
    avgSales: 25,
    daysRemaining: 0.6,
    suggested: 200,
    supplier: 'Farmacéutica GNP',
  },
  {
    id: 2,
    name: 'Losartán 50mg',
    currentStock: 8,
    avgSales: 18,
    daysRemaining: 0.4,
    suggested: 150,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 3,
    name: 'Paracetamol 500mg',
    currentStock: 50,
    avgSales: 30,
    daysRemaining: 1.6,
    suggested: 100,
    supplier: 'Laboratorios Pisa',
  },
]

export const mockPriceAdjustments: PriceAdjustment[] = [
  {
    id: 'ADJ-001',
    supplier: 'Laboratorios Pisa',
    date: '2026-05-10',
    purchaseId: 'ING-003',
    status: 'pending',
    items: 3,
    products: [
      {
        id: 1,
        name: 'Paracetamol 500mg',
        quantity: 100,
        previousCost: 45.0,
        newCost: 48.0,
        costChange: 6.67,
        previousSalePrice: 70.0,
        previousMargin: 35.56,
        newSalePrice: 75.0,
        newMargin: 35.42,
        lotNumber: 'LOT-2026-04-001',
      },
      {
        id: 2,
        name: 'Loratadina 10mg',
        quantity: 60,
        previousCost: 120.0,
        newCost: 130.0,
        costChange: 8.33,
        previousSalePrice: 180.0,
        previousMargin: 33.33,
        newSalePrice: 195.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-004',
      },
      {
        id: 3,
        name: 'Omeprazol 20mg',
        quantity: 75,
        previousCost: 95.0,
        newCost: 92.0,
        costChange: -3.16,
        previousSalePrice: 145.0,
        previousMargin: 34.48,
        newSalePrice: 140.0,
        newMargin: 34.29,
        lotNumber: 'LOT-2026-04-005',
      },
    ],
  },
  {
    id: 'ADJ-002',
    supplier: 'Farmacéutica GNP',
    date: '2026-05-08',
    purchaseId: 'ING-002',
    status: 'updated',
    items: 2,
    isEdited: true,
    editDate: '2026-05-09',
    products: [
      {
        id: 4,
        name: 'Ibuprofeno 400mg',
        quantity: 80,
        previousCost: 65.0,
        newCost: 62.0,
        costChange: -4.62,
        previousSalePrice: 100.0,
        previousMargin: 35.0,
        newSalePrice: 95.0,
        newMargin: 34.74,
        lotNumber: 'LOT-2026-04-002',
      },
      {
        id: 5,
        name: 'Amoxicilina 500mg',
        quantity: 50,
        previousCost: 180.0,
        newCost: 180.0,
        costChange: 0,
        previousSalePrice: 270.0,
        previousMargin: 33.33,
        newSalePrice: 270.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-003',
      },
    ],
  },
  {
    id: 'ADJ-003',
    supplier: 'Distribuidora Farmacéutica',
    date: '2026-05-05',
    purchaseId: 'ING-004',
    status: 'updated',
    items: 1,
    products: [
      {
        id: 6,
        name: 'Diclofenaco 100mg',
        quantity: 45,
        previousCost: 85.0,
        newCost: 88.0,
        costChange: 3.53,
        previousSalePrice: 130.0,
        previousMargin: 34.62,
        newSalePrice: 135.0,
        newMargin: 34.81,
        lotNumber: 'LOT-2026-04-006',
      },
    ],
  },
]

export const mockExtractedProducts: ProductFromXML[] = [
  {
    id: 1,
    name: 'Paracetamol 500mg',
    quantity: 100,
    newPrice: 48.0,
    previousPrice: 45.0,
    priceChange: 6.67,
    satKey: '50161501',
    lotNumber: 'LOT-2026-04-001',
    salePrice: 75.0,
    margin: 35,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 2,
    name: 'Ibuprofeno 400mg',
    quantity: 80,
    newPrice: 62.0,
    previousPrice: 65.0,
    priceChange: -4.62,
    satKey: '50161502',
    lotNumber: 'LOT-2026-04-002',
    salePrice: 95.0,
    margin: 30,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 3,
    name: 'Amoxicilina 500mg',
    quantity: 50,
    newPrice: 180.0,
    previousPrice: 180.0,
    priceChange: 0,
    satKey: '51101506',
    lotNumber: 'LOT-2026-04-003',
    salePrice: 270.0,
    margin: 40,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 4,
    name: 'Loratadina 10mg',
    quantity: 60,
    newPrice: 130.0,
    previousPrice: 120.0,
    priceChange: 8.33,
    satKey: '51101801',
    lotNumber: 'LOT-2026-04-004',
    salePrice: 195.0,
    margin: 38,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 5,
    name: 'Omeprazol 20mg',
    quantity: 75,
    newPrice: 92.0,
    previousPrice: 95.0,
    priceChange: -3.16,
    satKey: '51171701',
    lotNumber: 'LOT-2026-04-005',
    salePrice: 140.0,
    margin: 32,
    supplier: 'Laboratorios Pisa',
  },
]

export const mockExcelProducts: NewProductData[] = [
  {
    id: 1,
    name: 'Aspirina 500mg',
    satKey: '51121701',
    categoryId: '',
    departmentId: '',
    productTypeId: '',
    expiryDate: '',
    unitId: '',
    description: '',
    isRegistered: false,
  },
  {
    id: 2,
    name: 'Diclofenaco Gel',
    satKey: '53131626',
    categoryId: '',
    departmentId: '',
    productTypeId: '',
    expiryDate: '',
    unitId: '',
    description: '',
    isRegistered: false,
  },
]

// ---------------------------------------------------------
// 4. El Objeto de Servicio con todas las peticiones
// ---------------------------------------------------------

export const purchasesService = {
  // === MOCK METHODS (CURRENTLY USED) ===

  async getSuppliers() {
    return Promise.resolve(mockSuppliers)
  },

  async getProductEntries(params?: {
    search?: string
    supplier_id?: number
    status?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
  }): Promise<PurchaseApiResponse<PurchasePaginator>> {
    const response = await http.get('/purchases', { params })
    return response.data
  },

  async getProductEntry(id: number): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const response = await http.get(`/purchases/${id}`)
    return response.data
  },

  async createManualPurchase(payload: ManualPurchasePayload): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const response = await http.post('/purchases/manual', payload)
    return response.data
  },

  async downloadExcelTemplate(): Promise<Blob> {
    const response = await http.get('/purchases/excel/template', { responseType: 'blob' })
    return response.data
  },

  async previewExcel(file: File): Promise<PurchaseApiResponse<PurchaseExcelPreview>> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await http.post('/purchases/excel/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async createExcelPurchase(payload: ExcelPurchasePayload, file: File): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('reference_number', payload.reference_number)
    formData.append('supplier_id', String(payload.supplier_id))
    formData.append('purchase_date', payload.purchase_date)
    formData.append('entry_date', payload.entry_date)
    formData.append('payment_condition', payload.payment_condition)
    formData.append('payment_method_id', String(payload.payment_method_id))
    formData.append('items', JSON.stringify(payload.items))
    const response = await http.post('/purchases/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async getSuggestedProducts() {
    return Promise.resolve(mockSuggestedProducts)
  },

  async getPriceAdjustments() {
    return Promise.resolve(mockPriceAdjustments)
  },

  async processXmlMock() {
    return Promise.resolve(mockExtractedProducts)
  },

  async processExcelMock() {
    return Promise.resolve(mockExcelProducts)
  },


  // === BACKEND CONNECTION PREPARATION (TODO: USE THESE IN THE FUTURE) ===
  
  /*
  // Obtener proveedores
  async apiGetSuppliers(params?: { per_page?: number; search?: string; page?: number }) {
    const response = await http.get('/api/suppliers', { params }) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener un proveedor por ID
  async apiGetSupplier(id: number) {
    const response = await http.get(`/api/suppliers/${id}`) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Crear proveedor
  async apiCreateSupplier(payload: CreateSupplierPayload) {
    const response = await http.post('/api/suppliers', payload) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener compras/ingresos
  async apiGetPurchases(params?: { per_page?: number; search?: string; page?: number }) {
    const response = await http.get('/api/purchases', { params }) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Crear ingreso de mercancía (compra)
  async apiCreatePurchase(payload: CreateProductEntryPayload) {
    const response = await http.post('/api/purchases', payload) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener sugerencias de pedido
  async apiGetSuggestedOrders() {
    const response = await http.get('/api/purchases/suggested') // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener ajustes de precios
  async apiGetPriceAdjustments() {
    const response = await http.get('/api/purchases/adjustments') // TODO: CAMBIAR RUTA
    return response.data
  }
  */
}
